/**
 * 滑块验证兼容性诊断采集
 *
 * 只上报"验证没通过"的手势：一次失败一份报告，成功路径直接丢弃轨迹。
 * 报告含三部分，用于远程区分是哪种兼容性问题：
 *   env   浏览器环境（事件模型支持、触摸能力、缩放、弹窗宿主尺寸）
 *   geo   题面/滑轨实测尺寸（把手可移动范围为 0 时用户就是"拖不动"）
 *   drag + events  按下→移动→松手→提交的时间线与坐标（缺口位置由服务端补齐）
 */
import axios from 'axios'

const LOG_URL = '/user/login/slider/captcha/log'
// 单份轨迹保留的标记上限：超出后丢最早的，保证 down→up→resp 尾部一定完整
const MAX_EVENTS = 40
// 同一类失败在该时间窗内只上报一次，避免用户反复点击造成刷屏
const DEDUPE_WINDOW = 5000

let env = null
let trace = []
let failed = false
let startedAt = Date.now()
let session = ''
let lastSent = { reason: '', at: 0 }

function randId () {
  return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8)
}

function readEnv () {
  if (env) {
    return env
  }
  const nav = typeof navigator !== 'undefined' ? navigator : {}
  const docEl = typeof document !== 'undefined' ? document.documentElement : null
  env = {
    ua: nav.userAgent || '',
    platform: nav.platform || '',
    lang: nav.userLanguage || nav.language || '',
    vendor: nav.vendor || '',
    touchPoints: nav.maxTouchPoints === undefined ? null : nav.maxTouchPoints,
    // 事件模型支持情况：这是"拖不动/松手没反应"最直接的分组依据
    pointerEvent: typeof window.PointerEvent !== 'undefined',
    touchEvent: typeof window.TouchEvent !== 'undefined',
    mouseEvent: typeof window.MouseEvent !== 'undefined',
    captureApi: !!(window.Element && window.Element.prototype && window.Element.prototype.setPointerCapture),
    touchAction: !!(window.CSS && window.CSS.supports && window.CSS.supports('touch-action', 'none')),
    resizeObserver: typeof window.ResizeObserver !== 'undefined',
    passiveListener: detectPassive(),
    // 缩放会影响实测宽度：pageZoom / 根字号异常时把手行程可能算成 0
    dpr: window.devicePixelRatio || 1,
    vw: window.innerWidth || 0,
    vh: window.innerHeight || 0,
    sw: window.screen ? window.screen.width : 0,
    sh: window.screen ? window.screen.height : 0,
    rootW: docEl ? docEl.clientWidth : 0,
    rootFs: (docEl && window.getComputedStyle) ? window.getComputedStyle(docEl).fontSize : '',
    page: window.location ? window.location.pathname : ''
  }
  return env
}

// passive 默认值不同（Chrome 51+ 的 window touch 监听默认 passive）会让 preventDefault 静默失效，
// 表现为手指一拖就变成页面滚动：这里探测一次，把结论带进报告
function detectPassive () {
  try {
    let passive = null
    const opts = Object.defineProperty({}, 'passive', {
      get () {
        passive = true
        return true
      }
    })
    const handler = function () {}
    window.addEventListener('scPassiveProbe', handler, opts)
    window.removeEventListener('scPassiveProbe', handler, opts)
    return passive === true
  } catch (e) {
    return false
  }
}

function num (v) {
  // 全局 isFinite 兼容旧内核，NaN/Infinity 一律落成 null，不让诊断数据反过来抛异常
  return typeof v === 'number' && isFinite(v) ? Math.round(v * 10) / 10 : null
}

function push (key, detail, isFail) {
  try {
    if (trace.length >= MAX_EVENTS) {
      trace.splice(0, trace.length - MAX_EVENTS + 1)
    }
    trace.push({ t: Date.now() - startedAt, k: key, d: detail || null })
    if (isFail) {
      failed = true
    }
  } catch (e) { /* 采集异常不能影响验证流程 */ }
}

export function mark (key, detail) {
  push(key, detail, false)
}

export function markFail (key, detail) {
  push(key, detail, true)
}

// 验证通过时清空轨迹：成功场景一个字节都不上报
export function clearTrace () {
  trace = []
  failed = false
  startedAt = Date.now()
}

function build (reason, data) {
  const ctx = data || {}
  const drag = ctx.drag || {}
  return {
    v: 1,
    reason: reason || 'unknown',
    session: session || (session = randId()),
    ts: Date.now(),
    captchaId: ctx.captchaId || '',
    env: readEnv(),
    geo: ctx.geo || {},
    drag: {
      mode: drag.mode || '',
      startClientX: num(drag.startClientX),
      releaseClientX: num(drag.releaseClientX),
      lastMoveX: num(drag.lastMoveX),
      offset: num(drag.offset),
      pieceLeft: num(drag.pieceLeft),
      pct: num(drag.pct),
      moves: drag.moves || 0,
      sinceDown: drag.sinceDown || 0,
      sinceLastMove: drag.sinceLastMove || 0
    },
    result: ctx.result || {},
    events: trace.slice(0)
  }
}

function send (body, urgent) {
  try {
    const text = JSON.stringify(body)
    // 弹窗关闭/页面离开时 XHR 会被丢弃，只有 fetch+keepalive 能把最后一份带出去
    if (urgent && typeof window.fetch !== 'undefined') {
      const p = window.fetch(LOG_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: text,
        keepalive: true
      })
      if (p && p.catch) {
        p.catch(function () {})
      }
      return
    }
    axios.post(LOG_URL, body).catch(function () {})
  } catch (e) { /* 上报失败静默忽略 */ }
}

/**
 * 上报当前失败轨迹并重置
 * @param {string} reason 失败类型（no_up / no_move / check_fail ...）
 * @param {Object} data 组件现场 { captchaId, geo, drag, result }
 * @param {boolean} urgent 页面卸载场景走 fetch keepalive
 */
export function flush (reason, data, urgent) {
  try {
    if (!failed) {
      return
    }
    const now = Date.now()
    if (!urgent && lastSent.reason === reason && now - lastSent.at < DEDUPE_WINDOW) {
      // 同类失败短时间重复触发，清掉轨迹但不再发请求
      clearTrace()
      return
    }
    const body = build(reason, data)
    clearTrace()
    lastSent = { reason: reason, at: now }
    send(body, urgent)
  } catch (e) { /* ignore */ }
}
